import { MsalAuthenticationTemplate, useAzureAuth } from "@datamole/wds-azure-auth";
import { useEffect, useState, type ReactNode } from "react";

import { config } from "@/config";
import { AZURE_AD_INTERACTION_TYPE } from "@/features/auth";
import { AuthAzureErrorComponent } from "@/features/auth/AuthGuard/AuthAzureErrorComponent";
import { GetUser } from "@/features/auth/AuthGuard/GetUser";
import { LoginPage } from "@/pages/LoginPage";
import { WelcomePage } from "@/pages/WelcomePage";
import { useAppDispatch, useAppSelector } from "@/store";
import { authenticate, logout } from "@/store/slices/auth.slice";

const authenticationRequest = {
    scopes: [config.AZURE_AUTH_SCOPE],
};

type Props = {
    children: ReactNode;
};

export const AuthGuard = ({ children }: Props) => {
    const [isErrorUserNotFound, setIsErrorUserNotFound] = useState(false);

    const dispatch = useAppDispatch();
    const { isAuthenticated, authStrategy } = useAppSelector((state) => {
        return state.auth;
    });
    const { user: azureAuthUser, isInteractionInProgress, instance } = useAzureAuth();

    const resetUserNotFound = () => setIsErrorUserNotFound(false);

    const handleUserNotFound = async () => {
        try {
            instance.logoutRedirect({
                onRedirectNavigate: () => {
                    // Return false if you would like to stop navigation after local logout
                    setIsErrorUserNotFound(true);
                    dispatch(logout());
                    return false;
                },
            });
        } catch {
            // ignore clearing errors and continue with logout flow
        }
    };

    // Handle Redux auth state until ADFS is removed
    useEffect(() => {
        if (azureAuthUser?.idTokenClaims) {
            dispatch(authenticate({ authStrategy: "entraId" }));
        }
    }, [dispatch, azureAuthUser]);

    // ADFS
    if (isAuthenticated && authStrategy === "adfs") {
        return <GetUser resetUserNotFound={resetUserNotFound}>{children}</GetUser>;
    }

    // Azure Ad
    if (config.AZURE_AUTH_ENABLED && (azureAuthUser || isInteractionInProgress)) {
        return (
            <MsalAuthenticationTemplate
                interactionType={AZURE_AD_INTERACTION_TYPE}
                errorComponent={AuthAzureErrorComponent}
                loadingComponent={WelcomePage}
                authenticationRequest={authenticationRequest}
            >
                <GetUser addUserNotFound={handleUserNotFound} resetUserNotFound={resetUserNotFound}>
                    {children}
                </GetUser>
            </MsalAuthenticationTemplate>
        );
    }

    // Not authenticated
    return <LoginPage isErrorUserNotFound={isErrorUserNotFound} />;
};
